/**
 * O caminho de uma simulação, no mapa: os blocos por onde ela passou, em
 * ordem, e a ligação usada entre cada dois deles.
 *
 * O serviço devolve os passos pelo id do bloco e pela saída por onde cada um
 * saiu. A tela só acha, no mapa que já tem, os blocos e as ligações com esses
 * ids: nada é deduzido além disso.
 *
 * - A ligação é a da saída exata. Na escolha, um caso sem ligação própria
 *   segue pelo "outro caso", como o serviço faz. Sem saída que case, a
 *   ligação fica nula em vez de inventada.
 * - Um passo de um bloco que não está neste mapa (a simulação é de outra
 *   versão) fica de fora, e a ligação entre os vizinhos dele também: o caminho
 *   não passou direto de um para o outro.
 * - Os passos de dentro de um "para cada" não são blocos do mapa de cima:
 *   ficam de fora sem cortar o caminho.
 * - Parada numa espera, o bloco da espera entra como o último.
 * - Se o último passo foi de dentro de um "para cada", `dentroDe` diz qual:
 *   é ali que a simulação parou, e não depois dele.
 */

import { PROXIMO, saidaDaLigacao, type Configuracao } from './edicao';
import { ID_DO_COMECO } from './mapa';

export type PassoSimulado = { no: string | null; saida: string | null; resultado: string };

export type Percurso = {
  /** Os blocos, a começar pelo começo. */
  nos: string[];
  /** A ligação entre `nos[i]` e `nos[i + 1]`, pelo id da ligação no mapa; `null` se não há. */
  ligacoes: (string | null)[];
  /** O bloco onde a simulação falhou, se o serviço disse qual. */
  falhou: string | null;
  /** O "para cada" de onde veio o último passo, se ele foi de dentro de um. */
  dentroDe: string | null;
};

type Grafo = Configuracao['grafo'];

/** A ligação usada para sair de `de` por `saida` e chegar em `para`, ou `null`. */
function ligacaoUsada(grafo: Grafo, de: string, saida: string | null, para: string): string | null {
  const entre = grafo.ligacoes.filter((l) => l.de === de && l.para === para);
  const pela = (s: string): Grafo['ligacoes'][number] | undefined =>
    entre.find((l) => saidaDaLigacao(l) === s);
  const usada =
    saida === null
      ? entre.length === 1
        ? entre[0]
        : undefined
      : (pela(saida) ??
        // Na escolha, o caso que não tem ligação própria vai pelo "outro caso".
        (saida.startsWith('caso:') ? pela(PROXIMO) : undefined));
  return usada ? `${de}|${saidaDaLigacao(usada)}` : null;
}

export function percursoDaSimulacao(
  passos: readonly PassoSimulado[],
  grafo: Grafo,
  esperaEm: string | null = null,
): Percurso {
  const existe = new Set(grafo.nos.map((n) => n.id));
  // Cada bloco de dentro de um "para cada", com o "para cada" dele.
  const paiDoCorpo = new Map(
    grafo.nos.flatMap((n) =>
      n.tipo === 'para_cada' ? n.corpo.nos.map((c) => [c.id, n.id] as const) : [],
    ),
  );

  const nos = [ID_DO_COMECO];
  const ligacoes: (string | null)[] = [];
  let anterior: { no: string; saida: string | null } | null = null;
  let pulou = false;
  let dentroDe: string | null = null;
  const entrar = (no: string, saida: string | null): void => {
    ligacoes.push(
      pulou
        ? null
        : anterior === null
          ? grafo.inicio === no
            ? `${ID_DO_COMECO}|${PROXIMO}`
            : null
          : ligacaoUsada(grafo, anterior.no, anterior.saida, no),
    );
    nos.push(no);
    anterior = { no, saida };
    pulou = false;
    dentroDe = null;
  };

  for (const p of passos) {
    if (p.no === null) continue;
    if (!existe.has(p.no)) {
      // Dentro de um "para cada" não corta o caminho; um bloco de outra versão, sim.
      const pai = paiDoCorpo.get(p.no);
      if (pai === undefined) pulou = true;
      dentroDe = pai ?? null;
      continue;
    }
    entrar(p.no, p.saida);
  }
  if (esperaEm !== null && existe.has(esperaEm) && nos[nos.length - 1] !== esperaEm) {
    entrar(esperaEm, null);
  }

  const falhou = passos.find((p) => p.resultado === 'falhou' && p.no !== null && existe.has(p.no));
  return { nos, ligacoes, falhou: falhou?.no ?? null, dentroDe };
}
